/* eslint-disable react-hooks/exhaustive-deps */
import PropType from "prop-types";
import { useCallback, useEffect, useState, useMemo } from "react";
import Typography from "../../atoms/typography";
import FilterSection from "./filterSection/index";
import {
  getReplaceFiltersURL,
  removeFiltersParamsFromURL,
  parseAppliedFilters,
  buildPriceFilterString,
  buildFilterPayload,
  trackPriceFilterSelected,
} from "../../../utils/plp/filters";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { requestToFetchProductListingAction } from "../../../store/slices/plpSlice";
import { plpCleverTapEvents, plpPageConstants } from "../../../constants/pageConstants/plpConstants";
import { COUNTRIES, fnpPageType } from "../../../constants/common/common";
import { convertFilterObjToArray, toTitleCase, removeEmptyNullAndUndefinedFromObject } from "../../../utils/common";
import useAnalytics from "../../../analytics/useAnalytics";
import { GA4_EVENTS } from "../../../analytics/gaConstant";
import { usePathname } from "next/navigation";
import CookieUtil from "../../../utils/storage/cookieUtil";

/**
 * ProductFilter component for displaying and managing product filters.
 * @param {object} param0 - The props object.
 * @returns {JSX.Element}
 */
const ProductFilter = ({ facets, categoryId }) => {
  const { trackEvent } = useAnalytics();
  const pathname = usePathname();
  const [selectedOptions, setSelectedOptions] = useState({});
  const [priceRange, setPriceRange] = useState(null);
  const [selectedPriceRange, setSelectedPriceRange] = useState(null);
  const dispatch = useDispatch();
  const [initialLoad, setInitialLoad] = useState(true);
  const urlParams = useMemo(() => new URLSearchParams(typeof window === "object" ? window?.location?.search : ""), []);
  const { appliedFilters, appliedSort, pincode, isLoaded, catalogId, intlPincode } = useSelector(
    (state) => ({
      appliedFilters: state?.plp?.appliedFilters,
      appliedSort: state?.plp?.appliedSort,
      pincode: state?.locationLock?.selectedDomesticLocation?.pincode,
      isLoaded: state?.plp?.isLoaded,
      catalogId: state.appConfig.catalogId,
      intlPincode: state?.locationLock?.selectedIntLocation?.pincode,
    }),
    shallowEqual,
  );

  const priceFacet = facets?.find((facet) => facet.facetFieldName === "defaultPrice");

  /**
   * Handle filter changes including price range
   */
  const onSelectedOptions = useCallback(
    (options, sortOption, selectedPincode, intlPincode, priceRangeFilter = null) => {
      const appliedFilterArray = convertFilterObjToArray(options);

      // Add price range filter if provided
      const priceFilterString = buildPriceFilterString(priceFacet?.facetFieldName, priceRangeFilter);
      if (priceFilterString) {
        appliedFilterArray.push(priceFilterString);
      }

      // Get email from faul cookie
      const email = CookieUtil.getCookie("faul") || "";
      const fnpId = CookieUtil.getCookie("ab_user_id");

      // Check if this is a search page
      const isSearchPage = categoryId === plpPageConstants.SEARCH;
      const searchQuery = isSearchPage ? urlParams.get("qs") : null;

      window.scrollTo(0, 0);

      const payload = buildFilterPayload({
        appliedFilterArray,
        sortOption,
        selectedPincode,
        intlPincode,
        catalogId,
        categoryId,
        isSearchPage,
        searchQuery,
        pageSize: plpPageConstants.DESKTOP_PRODUCT_PER_PAGE,
        initialPage: plpPageConstants.INITIAL_PAGE,
        searchPageType: plpPageConstants.SEARCH,
        defaultCountry: COUNTRIES?.INDIA,
        email,
        fnpId,
      });

      dispatch(requestToFetchProductListingAction(removeEmptyNullAndUndefinedFromObject(payload)));

      if (appliedFilterArray?.length > 0) {
        setTimeout(() => {
          const currentParams = new URLSearchParams(window.location.search);
          const updatedUrl = getReplaceFiltersURL(currentParams, appliedFilterArray);
          const base = updatedUrl.includes("?") ? updatedUrl.split("?")[0] : updatedUrl;
          const newParams = new URLSearchParams(updatedUrl.split("?")[1] || "");

          // Preserve or add sortBy / sortOrder / qs
          ["sortBy", "sortOrder", "qs"].forEach((key) => {
            const val =
              sortOption?.[key] || currentParams.get(key) || (isSearchPage && key === "qs" ? searchQuery : "");
            if (val) newParams.set(key, val);
          });

          const finalUrl = `${base}?${newParams.toString()}`;
          window.history.replaceState({ ...window.history.state, as: finalUrl, url: finalUrl }, "", finalUrl);
        }, 100);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [facets, catalogId, categoryId, urlParams, pincode, intlPincode],
  );

  useEffect(() => {
    if (isLoaded) {
      if (
        Object?.keys(selectedOptions)?.length < 1 &&
        priceRange === null &&
        typeof window === "object" &&
        !initialLoad &&
        selectedPriceRange === null
      ) {
        const urlParams = new URLSearchParams(window?.location?.search);
        const newUrl = removeFiltersParamsFromURL(urlParams, "filter");
        window.history.replaceState({ ...window.history.state, as: newUrl, url: newUrl }, "", newUrl);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, selectedOptions, priceRange, selectedPriceRange]);

  useEffect(() => {
    if (!initialLoad) return;

    const { selectedOptionsFromFilters, priceRangeFromFilters } = parseAppliedFilters(appliedFilters);

    setSelectedOptions(selectedOptionsFromFilters);
    if (priceRangeFromFilters) {
      setPriceRange(priceRangeFromFilters);
      setSelectedPriceRange(priceRangeFromFilters); // Also set selectedPriceRange for FilterSection
    }
  }, [appliedFilters, initialLoad]);

  useEffect(() => {
    // Only trigger API call if not initial load and not currently changing filters
    if (!initialLoad) {
      onSelectedOptions(selectedOptions, appliedSort, pincode, intlPincode, selectedPriceRange);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOptions]);

  /**
   * Handle price range changes - immediate visual update, no API call
   */
  const onPriceChange = useCallback((newPriceRange) => {
    setPriceRange(newPriceRange);
  }, []);

  /**
   * Handle price range final selection - trigger API call
   */
  const onPriceChangeComplete = useCallback(
    (newPriceRange) => {
      setInitialLoad(false);
      setSelectedPriceRange(newPriceRange);

      // Track filter selected event for price
      trackPriceFilterSelected(newPriceRange, trackEvent, plpCleverTapEvents, pathname, fnpPageType.CATEGORY);

      onSelectedOptions(selectedOptions, appliedSort, pincode, intlPincode, newPriceRange);
    },
    [selectedOptions, appliedSort, pincode, intlPincode, onSelectedOptions, pathname, trackEvent],
  );

  /**
   * Handle filter option changes with dynamic value handling
   * @param {Event} event - The change event
   */
  const onOptionChange = (event) => {
    const { value, dataset } = event.target;
    setInitialLoad(false);

    // Find the option from facets to get displayName
    const facet = facets?.find((f) => f.facetFieldName === dataset.parentfilter);
    const option = facet?.facetOptions?.find((opt) => opt.value === value);
    const displayName = option?.displayName;

    // Special handling for price filter - use min-max format
    let filterValue;
    if (dataset.parentfilter === "listprice") {
      // For price filter, we don't use this function - it's handled by onPriceChange
      return;
    } else {
      // For all other facets, use displayName or fallback to value
      filterValue = displayName || value;
    }

    if (selectedOptions[value]) {
      // Remove filter if already selected
      const option = { ...selectedOptions };
      delete option[value];
      setSelectedOptions({ ...option });
    } else {
      // Add filter
      setSelectedOptions((prev) => ({
        ...prev,
        [value]: `${dataset.parentfilter}:${filterValue}`,
      }));

      // Track events with correct values
      trackEvent(
        plpCleverTapEvents.FILTER_SELECTED,
        {
          page_name: pathname,
          page_type: fnpPageType.CATEGORY,
          filter: `${dataset.parentfilter} : ${filterValue}`,
        },
        ["clevertap"],
      );
      trackEvent(
        GA4_EVENTS.FILTER_CLICKED,
        {
          active_filters: [...appliedFilters, `${dataset.parentfilter} : ${filterValue}`],
          selected_filter: filterValue,
        },
        ["ga4"],
      );
    }
  };

  /**
   * To Clear all the applied filters.
   */
  const onClearFilters = () => {
    setPriceRange(null);
    setSelectedPriceRange(null);
    setSelectedOptions({});
    setInitialLoad(false);
  };

  /**
   * Filtering the tabs which has more than zero filters available.
   */
  const getFilterTabs = () => {
    return facets?.map((filter) => {
      const totalCount = filter?.facetOptions?.reduce((acc, option) => acc + (option?.count || 0), 0);
      return (
        (totalCount > 0 || filter.facetFieldName === "defaultPrice") && (
          <FilterSection
            key={filter.facetFieldName}
            title={toTitleCase(filter?.facetFieldDisplayName)}
            options={filter.facetOptions}
            onOptionChange={onOptionChange}
            selectedOptions={selectedOptions}
            facetName={filter.facetFieldName}
            onPriceChange={onPriceChange}
            onPriceChangeComplete={onPriceChangeComplete}
            priceRange={selectedPriceRange}
          />
        )
      );
    });
  };
  return (
    <div
      className={`border-grey-100 sticky top-148 max-h-[calc(100vh-22vh)] w-full max-w-md overflow-x-hidden rounded-lg border-1 bg-white-900 scrollbar-thin`}
    >
      <div className="relative flex items-center justify-between px-20 py-16">
        <Typography Tag="h6" variant="subheading1" classes="text-20 font-500 text-fnp-500">
          Filter
        </Typography>
        {((selectedOptions && Object.keys(selectedOptions)?.length > 0) ||
          priceRange?.length > 1 ||
          selectedPriceRange?.length > 1) && (
          <Typography
            Tag="h6"
            variant="subheading1"
            classes="text-14 text-blue-100 font-500 cursor-pointer"
            onClick={onClearFilters}
            id="filter-clear-all"
          >
            Clear All
          </Typography>
        )}
      </div>
      <div className="mx-auto w-full">{getFilterTabs()}</div>
    </div>
  );
};

ProductFilter.propTypes = {
  categoryId: PropType.string.isRequired,
  facets: PropType.arrayOf(
    PropType.shape({
      facetFieldName: PropType.string.isRequired,
      facetFieldDisplayName: PropType.string.isRequired,
      facetOptions: PropType.arrayOf(
        PropType.shape({
          value: PropType.string.isRequired,
          displayName: PropType.string.isRequired,
          count: PropType.number.isRequired,
        }),
      ).isRequired,
    }),
  ).isRequired,
};

export default ProductFilter;
